import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, Shield, LogIn } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AdminHeader, TableWrap, Th, Td, Tr, StatusChip, Spinner, EmptyRow } from "@/components/admin/ui";
import { DEFAULT_TZ } from "@/lib/timezones";

const PAGE = 50;

export default function AuditLog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [actor, setActor] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get("/admin/audit-logs", { params: { limit: PAGE, skip: page * PAGE, action: q || undefined, actor: actor || undefined } })
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); })
      .catch((e) => { toast(getErrorMessage(e, "Load failed")); })
      .finally(() => setLoading(false));
  }, [page, q, actor]);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/admin" replace />;

  const pages = Math.max(1, Math.ceil(total / PAGE));

  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: DEFAULT_TZ })} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: DEFAULT_TZ })}`;
  };

  return (
    <div>
      <AdminHeader
        title="Audit Log"
        subtitle={`${total} recorded action${total === 1 ? "" : "s"} · admin & staff (times in Atlantic)`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input className="input-cyber pl-10 w-48" placeholder="Filter action" value={q}
                onChange={(e) => { setPage(0); setQ(e.target.value); }} data-testid="audit-action-filter" />
            </div>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input className="input-cyber pl-10 w-48" placeholder="Filter by email" value={actor}
                onChange={(e) => { setPage(0); setActor(e.target.value); }} data-testid="audit-actor-filter" />
            </div>
          </div>
        }
      />

      {loading ? <Spinner label="Loading audit log" /> : (
        <>
          <TableWrap>
            <thead><Tr><Th>When</Th><Th>Who</Th><Th>Role</Th><Th>Action</Th><Th>Endpoint</Th><Th>Status</Th></Tr></thead>
            <tbody>
              {items.length === 0 ? <EmptyRow colSpan={6} text="No audit entries yet" /> : items.map((a) => (
                <Tr key={a.id}>
                  <Td className="text-slate-300 whitespace-nowrap">{fmt(a.created_at)}</Td>
                  <Td className="text-white font-semibold" data-testid={`audit-actor-${a.id}`}>{a.actor_email || "—"}</Td>
                  <Td>{a.actor_role ? <StatusChip status={a.actor_role} /> : <span className="text-slate-500">—</span>}</Td>
                  <Td className="text-slate-200">
                    <span className="inline-flex items-center gap-1.5">
                      {a.path === "/api/auth/login" ? <LogIn className="w-3.5 h-3.5 text-cyan-400" /> : <Shield className="w-3.5 h-3.5 text-slate-500" />}
                      {a.action}
                    </span>
                  </Td>
                  <Td className="text-slate-500 text-xs font-mono">{a.method} {a.path}</Td>
                  <Td><span className="text-xs" style={{ color: a.status < 400 ? "#00ff9d" : "#ff2e97" }}>{a.status}</span></Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>

          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="neon-outline-btn px-3 py-2 disabled:opacity-40" data-testid="audit-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-slate-400 text-sm">Page {page + 1} / {pages}</span>
              <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
                className="neon-outline-btn px-3 py-2 disabled:opacity-40" data-testid="audit-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
